import { PlusIcon, LifebuoyIcon } from "@phosphor-icons/react";
import type { HelpRequestWithDetails } from "@reactive-resume/dto";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState } from "react";
import { Link } from "react-router";
import { Button, Skeleton } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

import {
  publicMessages,
  CATEGORY_META,
  isHelpCategory,
  STATUS_META,
} from "@/client/services/help/display";
import { useHelpRequests } from "@/client/services/help/queries";

import { NewHelpRequestDialog } from "./new-request-dialog";

dayjs.extend(relativeTime);
dayjs.locale("fr");

/** Demande encore active (OPEN ou IN_PROGRESS — plusieurs possibles). */
const isOpenish = (request: HelpRequestWithDetails) =>
  request.status === "OPEN" || request.status === "IN_PROGRESS";

const RequestCard = ({ request }: { request: HelpRequestWithDetails }) => {
  const meta = isHelpCategory(request.category)
    ? CATEGORY_META[request.category]
    : CATEGORY_META.OTHER;
  const status = STATUS_META[request.status] ?? STATUS_META.OPEN;
  const Icon = meta.icon;
  const messages = publicMessages(request.messages);
  const last = messages[messages.length - 1];

  return (
    <Link
      to={request.id}
      className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-100/60 sm:p-5"
    >
      <span className={cn("grid size-11 shrink-0 place-items-center rounded-xl", meta.tile)}>
        <Icon aria-hidden size={22} weight="duotone" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="truncate text-[15px] font-semibold text-slate-900">{request.subject}</h3>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
              status.badge,
            )}
          >
            {status.label}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-slate-500">
          <span className="font-mono font-medium tracking-wide text-slate-400">{request.code}</span>
          <span className="mx-1.5">·</span>
          {meta.label}
          <span className="mx-1.5">·</span>
          {messages.length > 0
            ? `${messages.length} message${messages.length > 1 ? "s" : ""}`
            : "En attente d'envoi"}
        </p>
        {last && (
          <p className="mt-1.5 line-clamp-1 text-[13px] leading-5 text-slate-600">
            <span
              className={cn(
                "font-medium",
                last.authorRole === "admin" ? "text-emerald-700" : "text-slate-500",
              )}
            >
              {last.authorRole === "admin" ? "Équipe MonCVpro : " : "Vous : "}
            </span>
            {last.body}
          </p>
        )}
      </div>

      <time
        className="hidden shrink-0 text-[11px] text-slate-400 sm:block"
        dateTime={dayjs(request.updatedAt).toISOString()}
      >
        {dayjs(request.updatedAt).fromNow()}
      </time>
    </Link>
  );
};

/**
 * Page Centre d'aide (liste des demandes) — design system user-space :
 * header duotone, cartes élévation douce, statuts sémantiques.
 */
export const UserSpaceHelpPage = () => {
  const { data: requests, isLoading, isError } = useHelpRequests();
  const [dialogOpen, setDialogOpen] = useState(false);

  const sorted = requests ?? [];
  const activeRequests = sorted.filter(isOpenish);
  const activeRequest = activeRequests[0] ?? null;

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 pb-24 pt-6 sm:px-6 lg:pb-6">
      <header className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 hidden size-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-600 sm:grid">
              <LifebuoyIcon aria-hidden size={22} weight="duotone" />
            </span>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-indigo-950 sm:text-2xl">
                Centre d'aide
              </h1>
              <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-500 sm:text-[13px]">
                Un problème avec un téléchargement, un paiement ou une fonction IA ? Ouvrez une
                demande et suivez la réponse de l'équipe ici.
              </p>
            </div>
          </div>

          <Button onClick={() => setDialogOpen(true)} className="gap-2 rounded-xl">
            <PlusIcon aria-hidden size={16} weight="bold" />
            Nouvelle demande
          </Button>
        </div>
      </header>

      {activeRequests.length > 0 && (
        <Link
          to={activeRequest.id}
          className="mb-4 flex items-center gap-3 rounded-2xl bg-amber-50 p-4 ring-1 ring-amber-200 transition-colors hover:bg-amber-100/70"
        >
          <span className="relative flex size-2.5 shrink-0">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-amber-400 opacity-60" />
            <span className="relative inline-flex size-2.5 rounded-full bg-amber-500" />
          </span>
          <p className="text-[13px] text-amber-900">
            {activeRequests.length === 1 ? (
              <>
                <span className="font-semibold">Demande en cours — {activeRequest.code}.</span>{" "}
                Suivez la conversation pour un suivi plus rapide.
              </>
            ) : (
              <>
                <span className="font-semibold">{activeRequests.length} demandes en cours.</span>{" "}
                Suivez leur traitement depuis la liste ci-dessous.
              </>
            )}
          </p>
        </Link>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-[88px] w-full rounded-2xl" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-2xl bg-red-50 p-4 text-[13px] text-red-700 ring-1 ring-red-200">
          Impossible de charger vos demandes. Rechargez la page ou réessayez plus tard.
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white/60 px-6 py-16 text-center">
          <span className="grid size-14 place-items-center rounded-2xl bg-indigo-50 text-indigo-500">
            <LifebuoyIcon aria-hidden size={28} weight="duotone" />
          </span>
          <h2 className="mt-4 text-[15px] font-semibold text-slate-900">
            Aucune demande pour le moment
          </h2>
          <p className="mt-1 max-w-sm text-[13px] leading-5 text-slate-500">
            Vous n'avez encore rien signalé. Si quelque chose bloque, décrivez-le nous — chaque
            demande reçoit une référence de suivi.
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            variant="outline"
            className="mt-5 gap-2 rounded-xl"
          >
            <PlusIcon aria-hidden size={16} weight="bold" />
            Ouvrir ma première demande
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </div>
      )}

      <NewHelpRequestDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        openRequest={activeRequest}
      />
    </div>
  );
};
